import { Box, CircularProgress, Divider, SxProps, Theme, Typography } from '@mui/material';
import { DynamicIcon } from 'src/components/DynamicIcon/dynamic-icon';
import {
  FloatingStripButton,
  FloatingStripGroup,
} from 'src/components/FloatingStrip/floating-strip';
import { ShortcutText } from 'src/components/ShortcutText/shortcut-text';
import { appShortcuts } from 'src/constants/appShortcuts';
import { BULK_OVERLAY_HINT_MIN_WIDTH } from 'src/pages/CustomViews/components/bulk-overlay';
import { formatCount } from 'src/utils/number';

const BAR_ICON_SIZE = 15;

const countSx: SxProps<Theme> = (theme) => ({
  padding: '0 0.375rem 0 0.625rem',
  fontSize: '0.78125rem',
  fontWeight: 500,
  lineHeight: 'normal',
  whiteSpace: 'nowrap',
  color: theme.palette.custom.OV.sys.onSurfaceVariant,
  '& b': { fontWeight: 600, color: theme.palette.custom.OV.sys.onBackground },
});

const keyHintSx: SxProps<Theme> = {
  display: 'none',
  [`@container (min-width: ${BULK_OVERLAY_HINT_MIN_WIDTH})`]: { display: 'inline-flex' },
};

type BulkActionBarProps = {
  selectedCount: number;
  totalCount: number;
  onActionsClick: () => void;
  onClearClick: () => void;
  isActionsLoading?: boolean;
};

export const BulkActionBar = ({
  selectedCount,
  totalCount,
  onActionsClick,
  onClearClick,
  isActionsLoading = false,
}: BulkActionBarProps) => (
  <FloatingStripGroup
    tone="raised"
    role="toolbar"
    aria-label="Bulk actions"
    data-testid="bulk-action-bar"
  >
    <Typography component="span" sx={countSx} data-testid="bulk-action-bar-count">
      <b>{formatCount(selectedCount)}</b> of {formatCount(totalCount)} selected
    </Typography>
    <FloatingStripButton
      iconOnly
      onClick={() => onClearClick()}
      startIcon={<DynamicIcon name="x" library="lucide" size={BAR_ICON_SIZE} />}
      aria-label="Clear selection"
      data-testid="bulk-action-bar-clear"
    />
    <Divider
      orientation="vertical"
      flexItem
      sx={(theme) => ({
        margin: '0.3125rem 0.125rem',
        borderColor: theme.palette.custom.OV.sys.outlineVariant,
      })}
    />
    <FloatingStripButton
      iconTone="accent"
      onClick={() => onActionsClick()}
      disabled={isActionsLoading}
      startIcon={
        isActionsLoading ? (
          <CircularProgress color="inherit" size={BAR_ICON_SIZE} />
        ) : (
          <DynamicIcon name="sparkles" library="lucide" size={BAR_ICON_SIZE} />
        )
      }
      data-testid="bulk-action-bar-actions"
    >
      <span>{isActionsLoading ? 'Copying markdown...' : 'Actions'}</span>
      {isActionsLoading ? null : (
        <Box component="span" sx={keyHintSx} data-testid="bulk-action-bar-key-hint">
          <ShortcutText size="xs" shortcut={appShortcuts.bulkActions.command} />
        </Box>
      )}
    </FloatingStripButton>
  </FloatingStripGroup>
);
